import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useLocation } from 'react-router-dom';
import { useChatPanel } from '../contexts/ChatPanelContext';
import ChatPanel from './ChatPanel';

/**
 * The one chat panel, drawn above the routes (App.js) so that going to another page does not
 * build it again: its messages, scroll position and input stay as they were. It shows while the
 * page on screen docks the chat (PageShell `chat`, whose MultiModalLayout keeps the room for it).
 */
export const DockedChatPanel = () => {
  const { pageDocksChat } = useChatPanel();
  return pageDocksChat ? <ChatPanel /> : null;
};

// Pages scroll the document by default; pass allowPageScroll={false} only for a
// page that locks itself to the viewport and manages its own scrolling.
const MultiModalLayout = ({ children, allowPageScroll = true }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const {
    shouldShowChatPanel,
    showChatPanel,
    hideChatPanel,
    chatPanelState
  } = useChatPanel();

  // Handle route changes and chat panel visibility
  useEffect(() => {
    const state = location.state;

    // Show chat panel if coming from a chat action
    if (state?.showChat) {
      showChatPanel(state.sourceRoute, state.chatParams, { startMinimized: false });
    }
    // Show chat panel by default on multi-modal pages (unless explicitly hidden)
    else if (!chatPanelState.isVisible && !state?.hideChat) {
      showChatPanel(undefined, null, { startMinimized: isMobile });
    }

    // Hide chat panel when navigating to chat page directly
    if (location.pathname === '/chat' && !state?.fromMultiModal) {
      hideChatPanel();
    }
  }, [location, showChatPanel, hideChatPanel, chatPanelState.isVisible, isMobile]);

  const showChat = shouldShowChatPanel();
  const chatWidth = showChat ? (isMobile ? 0 : (chatPanelState.isMinimized ? 50 : chatPanelState.width)) : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        height: allowPageScroll ? 'auto' : '100dvh',
        minHeight: '100vh',
        overflowX: 'clip', // `clip` keeps sticky children working (hidden would create a scroll container)
        overflowY: allowPageScroll ? 'visible' : 'hidden',
        backgroundColor: 'transparent',
        backgroundImage: 'none',
      }}
    >
      {/* Main Content Area; the panel itself is DockedChatPanel, fixed beside it */}
      <Box
        sx={{
          flex: 1,
          width: showChat ? `calc(100% - ${chatWidth}px)` : '100%',
          marginLeft: showChat ? `${chatWidth}px` : 0,
          overflowX: 'clip', // `clip` keeps sticky children working (hidden would create a scroll container)
          overflowY: allowPageScroll ? 'visible' : 'hidden',
          transition: 'margin-left 0.3s ease-in-out, width 0.3s ease-in-out',
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

export default MultiModalLayout;
